import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { push } from 'connected-react-router';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import classNames from 'classnames';

import { selectors as userSelectors } from '../../store/user';

import {
  actions as competitionsActions,
  selectors as competitionsSelectors,
} from '../../store/competitions';

import {
  actions as contentsActions,
  selectors as contentsSelectors,
} from '../../store/contents';

import { COMPETITION_TYPES, CONTENT_TYPES } from '../common/enums';
import {
  UserPropType,
  CompetitionPropType,
  ContentPropType,
} from '../common/types';

import Competitions from './Competitions';
import Contents from './Contents';

const messages = defineMessages({
  title: {
    id: 'home-title',
    defaultMessage: 'Kenguru',
  },
});

const defaultGradient = 'linear-gradient(transparent, hsla(0, 0%, 0%, 0.5))';
const gradientHeight = 50;

class Home extends Component {
  frameRequest = null;

  static propTypes = {
    intl: PropTypes.object.isRequired,
    fetchCompetitions: PropTypes.func.isRequired,
    fetchContents: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    user: UserPropType,
    competitions: PropTypes.arrayOf(CompetitionPropType),
    competitionsLoading: PropTypes.bool.isRequired,
    contents: PropTypes.arrayOf(ContentPropType),
    contentsLoading: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    user: null,
    competitions: [],
    contents: [],
  };

  state = {
    fadeGradient: defaultGradient,
    code: '',
  };

  componentDidMount() {
    this.props.fetchCompetitions({
      type: COMPETITION_TYPES.KENGURU,
      is_public: true,
    });

    this.props.fetchContents();
    window.addEventListener('scroll', this.onScroll, true);
  }

  componentWillUnmount() {
    window.cancelAnimationFrame(this.frameRequest);
    window.removeEventListener('scroll', this.onScroll, true);
  }

  onScroll = () => {
    const { fadeGradient } = this.state;

    const distance =
      document.body.offsetHeight - (window.innerHeight + window.scrollY);

    if (distance > gradientHeight && fadeGradient !== defaultGradient) {
      this.setState({ fadeGradient: defaultGradient });
    } else if (distance <= gradientHeight) {
      const inverse = gradientHeight - distance;
      this.setState({
        fadeGradient: `linear-gradient(transparent ${inverse}px, hsla(0, 0%, 0%, 0.5))`,
      });
    }

    if (distance < 5 * gradientHeight) {
      this.frameRequest = window.requestAnimationFrame(this.onScroll);
    }
  };

  onCodeChange = event => {
    this.setState({ code: event.target.value });
  };

  onStudentLogin = e => {
    e.preventDefault();
    this.props.push(`/auth/student?code=${this.state.code}`);
  };

  render() {
    const { locale, formatMessage } = this.props.intl;

    const {
      user,
      competitions,
      competitionsLoading,
      contents,
      contentsLoading,
    } = this.props;

    const { fadeGradient, code } = this.state;

    const isStudent = user && user.isStudent();
    const isManager = user && user.isManager();
    const isAdmin = user && user.isAdmin();
    const isOrganizer = user && !user.isAdmin() && user.isOrganizer();
    const isTeacher =
      user && !user.isAdmin() && !user.isOrganizer() && user.isTeacher();

    const dateContent = contents.find(
      c => c.type === CONTENT_TYPES.HOME_TITLE_DATE
    );

    const sectionContents = contents.filter(
      c => c.type === CONTENT_TYPES.HOME_SECTION
    );

    return (
      <div className="Home">
        <div className="Home-fade" style={{ backgroundImage: fadeGradient }} />

        <div className="Home-hero text-center">
          <div className="Home-hero-title">
            <h5 className="Home-hero-title-top text-left text-primary ml-1 ml-sm-2 my-0">
              <FormattedMessage
                id="home-title-description"
                defaultMessage="Kansainvälinen matematiikkakilpailu"
              />
            </h5>

            <h1
              className={classNames(
                'Home-hero-title-main',
                'text-uppercase',
                'my-0',
                { 'extra-top': locale === 'sv' }
              )}
            >
              {[...formatMessage(messages.title)].map((c, i) => (
                <span
                  key={i /* eslint-disable-line react/no-array-index-key */}
                  className={classNames({
                    'text-primary': i % 2 === 0,
                    'text-warning': i % 2 === 1,
                  })}
                >
                  {c}
                </span>
              ))}
            </h1>

            <div className="Home-hero-title-bottom mt-2 px-3 d-flex justify-content-between">
              <a href="#contents">
                <FormattedMessage
                  id="home-title-link"
                  defaultMessage="Lue lisää"
                />
              </a>

              <span>{dateContent && dateContent.body[locale]}</span>
            </div>
          </div>
        </div>

        <div className="Home-actions row justify-content-center">
          {isStudent && (
            <div className="col-12 px-4 col-md-10 px-md-0 col-lg-8 col-xl-6 col-xxl-4">
              <div className="card">
                <div className="card-body">
                  <div className="Home-actions-student-logo" />

                  <h3 className="card-title text-center">
                    <FormattedMessage
                      id="home-actions-student-header"
                      defaultMessage="Oppilas"
                    />
                  </h3>

                  <div className="card-text">
                    <p className="mb-3">
                      <FormattedMessage
                        id="home-actions-authenticated-student-content"
                        defaultMessage="Olet kirjautunut sisään oppilaana. Pääset jatkamaan kilpailun tekemistä allaolevasta napista."
                      />
                    </p>

                    <Link
                      to="/kilpailu"
                      className="btn btn-primary btn-block btn-lg"
                    >
                      <FormattedMessage
                        id="home-actions-authenticated-student-link"
                        defaultMessage="Jatka kilpailun tekemistä"
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isManager && (
            <div className="col-12 px-4 col-md-10 px-md-0 col-lg-8 col-xl-6 col-xxl-4">
              <div className="card">
                <div className="card-body">
                  <div
                    className={classNames({
                      'Home-actions-admin-logo': isAdmin,
                      'Home-actions-organizer-logo': isOrganizer,
                      'Home-actions-teacher-logo': isTeacher,
                    })}
                  />

                  <h3 className="card-title text-center">
                    {isAdmin && (
                      <FormattedMessage
                        id="home-actions-admin-header"
                        defaultMessage="Ylläpitäjä"
                      />
                    )}

                    {isOrganizer && (
                      <FormattedMessage
                        id="home-actions-organizer-header"
                        defaultMessage="Kisajärjestäjä"
                      />
                    )}

                    {isTeacher && (
                      <FormattedMessage
                        id="home-actions-teacher-header"
                        defaultMessage="Opettaja"
                      />
                    )}
                  </h3>

                  <div className="card-text">
                    <p className="mb-3">
                      {isAdmin && (
                        <FormattedMessage
                          id="home-actions-authenticated-admin-content"
                          defaultMessage="Olet kirjautunut sisään ylläpitäjänä. Pääset hallinnoimaan järjestelmää ja tutkimaan tarkempia tietoja allaolevasta napista."
                        />
                      )}

                      {isOrganizer && (
                        <FormattedMessage
                          id="home-actions-authenticated-organizer-content"
                          defaultMessage="Olet kirjautunut sisään kisajärjestäjänä. Pääset hallinnoimaan kilpailujasi ja tutkimaan tarkempia tietoja allaolevasta napista."
                        />
                      )}

                      {isTeacher && (
                        <FormattedMessage
                          id="home-actions-authenticated-teacher-content"
                          defaultMessage="Olet kirjautunut sisään opettajana. Pääset hallinnoimaan ryhmiäsi ja tutkimaan tarkempia tietoja allaolevasta napista."
                        />
                      )}
                    </p>

                    <Link
                      to="/dashboard"
                      className="btn btn-primary btn-block btn-lg"
                    >
                      <FormattedMessage
                        id="home-actions-authenticated-link"
                        defaultMessage="Hallintapaneeliin"
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!user && (
            <>
              <div className="col-12 px-4 col-md-6 pl-md-4 pr-md-0 col-xl-5 col-xxl-3">
                <div className="card">
                  <div className="card-body">
                    <div className="Home-actions-student-logo" />

                    <h3 className="card-title text-center">
                      <FormattedMessage
                        id="home-actions-student-header"
                        defaultMessage="Oppilas"
                      />
                    </h3>

                    <div className="card-text">
                      <p className="mb-3">
                        <FormattedMessage
                          id="home-actions-student-content-1"
                          defaultMessage="Olet saanut opettajaltasi lapun joka näyttää suurinpiirtein tältä:"
                        />
                      </p>

                      <div className="col-sm-10 mx-auto">
                        <div className="Home-actions-code-example col">
                          <div className="row px-2 py-2">
                            <div className="col-6">Pomppiva Kenguru</div>
                            <div className="col-6">nhvh6vj2</div>
                          </div>
                        </div>
                      </div>

                      <p className="mt-3">
                        <FormattedMessage
                          id="home-actions-student-content-2"
                          defaultMessage="Syötä lapun oikeassa reunassa oleva koodi aloittaaksesi kilpailun tekemisen!"
                        />
                      </p>

                      <form onSubmit={this.onStudentLogin}>
                        <div className="form-group">
                          <div className="row">
                            <div className="col-9 pr-0">
                              <input
                                type="text"
                                name="code"
                                className="Home-actions-student-input form-control"
                                placeholder="Henkilökohtainen koodisi"
                                onChange={this.onCodeChange}
                                value={code}
                                required
                              />
                            </div>

                            <div className="col-3 pl-0">
                              <button
                                type="submit"
                                className="Home-actions-student-button btn btn-primary btn-block btn-lg"
                                disabled={!code}
                              >
                                <FormattedMessage
                                  id="home-actions-student-link"
                                  defaultMessage="Aloita"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 px-4 col-md-6 col-xl-5 col-xxl-3">
                <div className="card">
                  <div className="card-body">
                    <div className="Home-actions-teacher-logo" />

                    <h3 className="card-title text-center">
                      <FormattedMessage
                        id="home-actions-teacher-header"
                        defaultMessage="Opettaja"
                      />
                    </h3>
                    <div className="card-text">
                      <p>
                        <FormattedMessage
                          id="home-actions-teacher-content"
                          defaultMessage="Kirjaudu sisään kätevästi käyttämällä MPASSid:tä tai luomalla uusi tunnus ja ilmoita oma ryhmäsi kilpailuun!"
                        />
                      </p>

                      <Link
                        to="/auth/login"
                        className="btn btn-primary btn-block btn-lg"
                      >
                        <FormattedMessage
                          id="home-actions-teacher-link"
                          defaultMessage="Kirjaudu sisään"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="container">
          <hr className="mb-4" />

          <div className="row">
            <div className="col-sm-4 mb-4 mb-sm-0 d-flex align-items-center">
              <div className="Home-archive-logo" />
            </div>

            <div className="col-sm-8">
              <h3 className="mb-3">
                <FormattedMessage
                  id="home-archive-header"
                  defaultMessage="Arkisto"
                />
              </h3>

              <p>
                <FormattedMessage
                  id="home-archive-content"
                  defaultMessage="Haluatko tutkia tai harjoitella vanhoja Kengurutehtäviä? Arkistossa voit hakea tehtäviä esimerkiksi helppouden tai aihealueen mukaan, sekä tehdä vanhoja Kengurukisoja ja nähdä miten olisit pärjännyt!"
                />
              </p>

              <Link
                to="/home/archive"
                className="btn btn-primary btn-block btn-lg"
              >
                <FormattedMessage
                  id="home-archive-link"
                  defaultMessage="Arkistoon"
                />
              </Link>
            </div>
          </div>

          <Competitions
            competitions={competitions}
            loading={competitionsLoading}
          />

          <Contents
            locale={locale}
            contents={sectionContents}
            loading={contentsLoading}
          />
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    user: userSelectors.currentUser(state),
    competitions: competitionsSelectors.competitions(state),
    competitionsLoading: competitionsSelectors.loading(state),
    contents: contentsSelectors.contents(state),
    contentsLoading: contentsSelectors.loading(state),
  }),
  dispatch =>
    bindActionCreators(
      {
        fetchCompetitions: competitionsActions.fetchAll,
        fetchContents: contentsActions.fetchAll,
        push,
      },
      dispatch
    ),
  (state, dispatch, props) => ({
    ...state,
    competitions: state.competitions.filter(
      c => c.type === COMPETITION_TYPES.KENGURU && c.is_public
    ),
    ...dispatch,
    ...props,
  })
)(injectIntl(Home));
